import React from "react";
import { useTranslation } from "react-i18next";
import { useSnapshot } from "valtio";
import { mutate } from "swr";

import { store as userStore } from "@components/VerticalLayout/store";
import { showSuccessToast } from "@components/Common/SuccessToast";
import { showErrorToast } from "@components/Common/ErrorToast";

import { useFetchGlobal } from "../../../../api/hooks";
import {
    FetchGlobalFn,
    PrinterRowData,
    PrinterSettingConfig,
    PrinterSettingDataBlob,
    PrinterSettingUserIds,
} from "../types";
import { orchestratorStore } from "../stores/orchestrator";
import { buildSavePayload } from "./savePayload";

const HTTP_POST = "POST";
const CONTENT_TYPE_JSON = "application/json";
const UUID_USER_STORAGE_KEY = "uuidUser";

export type UseSavePrinterSettingParams = {
    config: PrinterSettingConfig;
    rowData: PrinterRowData;
    oneShop: boolean;
};

export type UseSavePrinterSettingResult = {
    save: () => Promise<boolean>;
};

export function useSavePrinterSetting({
    config,
    rowData,
    oneShop,
}: UseSavePrinterSettingParams): UseSavePrinterSettingResult {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();
    const { shopID, userID, franchiseID, operatorID } = useSnapshot(userStore);
    const {
        dataGeneral,
        dataDivers,
        dataKitchenTicket,
        dataCustomerTicket,
        dataFirstTicket,
        dataSecondTicket,
        dataThreedTicket,
        dataFourthTicket,
        dataFifthTicket,
    } = useSnapshot(orchestratorStore);

    const save = React.useCallback(async (): Promise<boolean> => {
        const shopId = oneShop ? shopID.toString() : rowData.shopId ?? "";

        const dataBlob: PrinterSettingDataBlob = {
            customer_ticket: { ...dataCustomerTicket },
            divers: { ...dataDivers },
            general: { ...dataGeneral },
            Kitchen_ticket: { ...dataKitchenTicket },
            modelKitchenTicket: {
                model_1: { ...dataFirstTicket },
                model_2: { ...dataSecondTicket },
                model_3: { ...dataThreedTicket },
                model_4: { ...dataFourthTicket },
                model_5: { ...dataFifthTicket },
            },
        };

        const userIds: PrinterSettingUserIds = {
            userId: userID,
            shopId,
            franchiseId: franchiseID,
            operatorId: operatorID,
            uuidUser: localStorage.getItem(UUID_USER_STORAGE_KEY),
        };

        if (config.customSave) {
            return config.customSave({
                rowData,
                dataBlob,
                userIds,
                fetchGlobal: fetchGlobal as FetchGlobalFn,
                t,
            });
        }

        const apiUrl = config.endpoints.save;
        const savedData = buildSavePayload(config, rowData, dataBlob, userIds);

        try {
            const data: { error?: unknown } = await fetchGlobal(apiUrl, {
                method: HTTP_POST,
                headers: { "Content-Type": CONTENT_TYPE_JSON },
                body: JSON.stringify(savedData),
            });
            mutate(apiUrl, data);
            if (data.error) {
                throw new Error("Error while saving selection!");
            }
            showSuccessToast(`${t("Registration successfully")!}`);
            return true;
        } catch {
            showErrorToast(`${t("There's an error")}!`);
            return false;
        }
    }, [
        config,
        rowData,
        oneShop,
        shopID,
        userID,
        franchiseID,
        operatorID,
        dataGeneral,
        dataDivers,
        dataKitchenTicket,
        dataCustomerTicket,
        dataFirstTicket,
        dataSecondTicket,
        dataThreedTicket,
        dataFourthTicket,
        dataFifthTicket,
        t,
        fetchGlobal,
    ]);

    return { save };
}
